import { FaArrowLeft } from "react-icons/fa";
import BackendScreenerTable, { ColumnConfig } from "../screener/BackendScreenerTable.tsx";
import PremiumAccessPage from "../PremiumAccessPage.tsx";
import ViewModeToggle from "../common/ViewModeToggle.tsx";
import ChartGridView from "../common/ChartGridView.tsx";
import ScreenerFilterBar from "../common/ScreenerFilterBar.tsx";

import { useNavigate } from "react-router-dom";
import React, { useState } from "react";
import '../SwingScreener.css';
import { usePremium } from "../../hooks/usePremium.ts";
import useScreenerData from "../../hooks/useScreenerData.ts";
import useScreenerHistory from "../../hooks/useScreenerHistory.ts";
import { ScreenerStockFilters } from "../../services/screenerApi.ts";
import { SCREENER_PAGE_SIZE, SCREENER_HISTORY_PAGE_SIZE } from "../../constants/screener.ts";
import PostCaptureInfo from "../common/PostCaptureInfo.tsx";


const BreakoutScanner = () => {

    const navigate = useNavigate();
    const isPremium = usePremium();
    const [activeTab, setActiveTab] = useState<'stocks' | 'insights'>(!isPremium ? 'insights' : 'stocks');
    const [viewMode, setViewMode] = useState<'list' | 'chart'>('list');
    const [insightsViewMode, setInsightsViewMode] = useState<'list' | 'chart'>('list');
    const [filters, setFilters] = useState<ScreenerStockFilters>({});
    const { data: stocksData, loading: stocksLoading, error: stocksError,
            currentPage, totalPages, totalItems, setCurrentPage } = useScreenerData('BREAKOUT_AI', isPremium, filters);
    const { data: historyData, loading: historyLoading, error: historyError,
            currentPage: historyPage, totalPages: historyTotalPages, totalItems: historyTotalItems,
            setCurrentPage: setHistoryPage } = useScreenerHistory('BREAKOUT_AI');

    // Columns for stocks tab
    const stocksColumns: ColumnConfig[] = [
        { key: 'stockName', label: 'Stock Name', visible: true },
        { key: 'symbol', label: 'Symbol', visible: true },
        { key: 'capturedPrice', label: 'Captured Price', visible: true },
        { key: 'capturedOn', label: 'Captured On', visible: true },
    ];

    // Columns for backtest/history tab with maxUpside
    const historyColumns: ColumnConfig[] = [
        { key: 'stockName', label: 'Stock Name', visible: true },
        { key: 'symbol', label: 'Symbol', visible: true },
        { key: 'capturedPrice', label: 'Captured Price', visible: true },
        { key: 'currentPrice', label: 'Current Price', visible: true },
        { key: 'maxUpside', label: 'Max Upside %', visible: true },
        { key: 'capturedOn', label: 'Captured On', visible: true },
    ];

    return (
        <>
            <div className="main-content">
                {/* Header */}
                <div className="chart-header">
                    <button className="back-btn" onClick={() => navigate(-1)}>
                        <FaArrowLeft />
                    </button>
                    <h2 className="chart-title">Breakout AI Scanner</h2>
                </div>

                {/* Tabs */}
                <div className="scanner-tabs">
                    <button
                        className={`scanner-tab ${activeTab === 'stocks' ? 'active' : ''}`}
                        onClick={() => setActiveTab('stocks')}
                    >
                        Stocks
                    </button>
                    {/* Post Capture Insights tab — hidden temporarily */}
                    {/* <button
                        className={`scanner-tab ${activeTab === 'insights' ? 'active' : ''}`}
                        onClick={() => setActiveTab('insights')}
                    >
                        Post Capture Insights (Backtest)
                    </button> */}
                </div>

                {/* View Mode Toggle + Filters - stocks tab */}
                {activeTab === 'stocks' && isPremium && (
                    <div className="screener-toolbar">
                        <ViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
                        <div className="screener-toolbar-filter">
                            <ScreenerFilterBar onApply={setFilters} />
                        </div>
                    </div>
                )}

                {/* View Mode Toggle - insights/backtest tab */}
                {activeTab === 'insights' && (
                    <ViewModeToggle viewMode={insightsViewMode} onViewModeChange={setInsightsViewMode} />
                )}

                {/* Tab Content */}
                {activeTab === 'stocks' && (
                    !isPremium ? (<PremiumAccessPage />) : (
                        viewMode === 'list' ? (
                            <BackendScreenerTable
                                screenerCode="BREAKOUT_AI"
                                pageSize={SCREENER_PAGE_SIZE}
                                columns={stocksColumns}
                                viewChartButtonEnable={false}
                                data={stocksData}
                                loading={stocksLoading}
                                error={stocksError}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                            />
                        ) : (
                            <ChartGridView
                                isActive={activeTab === 'stocks' && viewMode === 'chart'}
                                data={stocksData}
                                loading={stocksLoading}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalItems={totalItems}
                                pageSize={SCREENER_PAGE_SIZE}
                                onPageChange={setCurrentPage}

                                screenerCode="BREAKOUT_AI"
                                smaValues={[21, 50]}
                            />
                        )
                    )
                )}

                {activeTab === 'insights' && (
                    <>
                    <PostCaptureInfo onViewLive={() => setActiveTab('stocks')} />
                    {insightsViewMode === 'list' ? (
                        <BackendScreenerTable
                            screenerCode="BREAKOUT_AI"
                            pageSize={SCREENER_HISTORY_PAGE_SIZE}
                            columns={historyColumns}
                            viewChartButtonEnable={false}
                            data={historyData}
                            loading={historyLoading}
                            error={historyError}
                            currentPage={historyPage}
                            totalPages={historyTotalPages}
                            totalItems={historyTotalItems}
                            onPageChange={setHistoryPage}
                        />
                    ) : (
                        <ChartGridView
                            isActive={activeTab === 'insights' && insightsViewMode === 'chart'}
                            data={historyData}
                            loading={historyLoading}
                            currentPage={historyPage}
                            totalPages={historyTotalPages}
                            totalItems={historyTotalItems}
                            pageSize={SCREENER_HISTORY_PAGE_SIZE}
                            onPageChange={setHistoryPage}
                            isAIScreener={true}
                            screenerCode="BREAKOUT_AI"
                            smaValues={[21, 50]}
                        />
                    )}
                    </>
                )}
            </div>
        </>
    );
}

export default BreakoutScanner;
